import {FC, useCallback, useEffect, useRef, useState} from 'react'
import {getLogger, isJobFinished} from '../../../../shared/utils'

import {
  countStreamEvents,
  fetchSessionLogs,
  LogStreamPanel,
  MAX_STEP_EVENTS,
  mergeStreamPanels,
  toStreamPanels,
} from '../core/jobLogs'

const GLOBAL_MAX_MESSAGES = MAX_STEP_EVENTS
const REFRESH_INTERVAL_MS = 60_000

interface LogRetrievalResult {
  streams: LogStreamPanel[]
  latestTimestamp: number
  limitReached: boolean
  totalRetrieved: number
  /** False when the session isn't the caller's or doesn't exist (the endpoint's 404). */
  found: boolean
}

function formatTimestamp(ts: number): string {
  return new Date(ts).toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'UTC',
  })
}

/**
 * The session's step streams with events newer than `afterTimestamp` (0 for all), up to
 * `maxMessages` across them. The session's own stream belongs to the Job Messages tab, so it is
 * not requested here.
 */
async function fetchLogs(
  sessionId: string,
  afterTimestamp: number,
  maxMessages: number
): Promise<LogRetrievalResult> {
  const logs = await fetchSessionLogs(sessionId, {
    after: afterTimestamp,
    limit: maxMessages,
    sessionLimit: 0,
  })
  return {
    streams: toStreamPanels(logs.streams),
    latestTimestamp: logs.latestTimestamp,
    limitReached: logs.limitReached,
    totalRetrieved: countStreamEvents(logs.streams),
    found: logs.found,
  }
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const MONO_FONT =
  "var(--ob-font-mono, 'SF Mono', Monaco, Menlo, Consolas, 'Ubuntu Mono', monospace)"

interface StreamPanelProps {
  stream: LogStreamPanel
  onToggle: (streamName: string) => void
}

const StreamPanel: FC<StreamPanelProps> = ({stream, onToggle}) => {
  return (
    <div style={{marginBottom: '4px'}}>
      {/* Collapsible header */}
      <div
        onClick={() => onToggle(stream.streamName)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 12px',
          backgroundColor: 'var(--ob-gray-200)',
          borderRadius: stream.expanded ? '6px 6px 0 0' : '6px',
          cursor: 'pointer',
          userSelect: 'none',
          borderBottom: stream.expanded ? '1px solid var(--ob-gray-300)' : 'none',
        }}
      >
        <span style={{fontWeight: 600, fontSize: 'var(--ob-font-size-sm)', flexShrink: 0}}>
          {stream.expanded ? '\u25BC' : '\u25B6'}
        </span>
        <span style={{fontWeight: 600, fontSize: 'var(--ob-font-size-sm)', flex: 1}}>
          {stream.displayName}
        </span>
        <span style={{fontSize: 'var(--ob-font-size-xs)', color: 'var(--ob-text-secondary)'}}>
          ({stream.messages.length} messages)
        </span>
      </div>

      {/* Content area */}
      {stream.expanded && (
        <div
          style={{
            fontFamily: MONO_FONT,
            fontSize: 'var(--ob-font-size-sm)',
            backgroundColor: 'var(--ob-gray-50)',
            border: '1px solid var(--ob-gray-300)',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            overflow: 'auto',
            maxHeight: '500px',
            lineHeight: '1.5',
          }}
        >
          {stream.messages.map((msg, idx) => {
            const isError = msg.message.toLowerCase().includes('error')
            const bgColor = isError
              ? 'var(--ob-log-error-bg, #ffe6e6)'
              : idx % 2 === 0
                ? 'var(--ob-log-stripe-bg, #f5f5f5)'
                : 'var(--ob-card-bg)'

            return (
              <div
                key={`${msg.timestamp}-${idx}`}
                style={{
                  padding: '2px 12px',
                  backgroundColor: bgColor,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                <span style={{color: 'var(--ob-text-secondary)', marginRight: '8px'}}>
                  {formatTimestamp(msg.timestamp)}
                </span>
                {msg.message}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Embeddable tab content component
// ---------------------------------------------------------------------------

interface CloudLogsTabContentProps {
  sessionId: string
  job: Record<string, unknown>
}

const CloudLogsTabContent: FC<CloudLogsTabContentProps> = ({sessionId, job}) => {
  const [streamPanels, setStreamPanels] = useState<LogStreamPanel[]>([])
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [limitReached, setLimitReached] = useState(false)
  const [totalMessages, setTotalMessages] = useState(0)
  const [noStreamsYet, setNoStreamsYet] = useState(false)
  const [notFound, setNotFound] = useState(false)

  const lastTimestampRef = useRef(0)
  const totalMessagesRef = useRef(0)
  const refreshIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const mountedRef = useRef(true)

  const jobFinished = isJobFinished(job)

  const addToTotal = useCallback((count: number) => {
    totalMessagesRef.current += count
    setTotalMessages(totalMessagesRef.current)
    return totalMessagesRef.current
  }, [])

  const stopAutoRefresh = useCallback(() => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current)
      refreshIntervalRef.current = null
    }
  }, [])

  const startAutoRefresh = useCallback(() => {
    stopAutoRefresh()

    refreshIntervalRef.current = setInterval(async () => {
      if (!mountedRef.current) return

      try {
        const result = await fetchLogs(sessionId, lastTimestampRef.current, GLOBAL_MAX_MESSAGES)

        if (!mountedRef.current) return

        if (result.streams.length > 0) {
          setStreamPanels((prev) => mergeStreamPanels(prev, result.streams))
          lastTimestampRef.current = result.latestTimestamp
          if (addToTotal(result.totalRetrieved) >= GLOBAL_MAX_MESSAGES) {
            setLimitReached(true)
            stopAutoRefresh()
          }
          setNoStreamsYet(false)
        }
      } catch (err) {
        getLogger().error('Error during auto-refresh:', err)
      }
    }, REFRESH_INTERVAL_MS)
  }, [sessionId, stopAutoRefresh, addToTotal])

  // Initial load
  useEffect(() => {
    if (!sessionId) return
    mountedRef.current = true

    const load = async () => {
      try {
        const result = await fetchLogs(sessionId, 0, GLOBAL_MAX_MESSAGES)

        if (!mountedRef.current) return

        if (!result.found) {
          // Not the caller's session, or no such session: nothing to show, and nothing to wait for.
          setNotFound(true)
          setLoading(false)
          return
        }

        if (result.streams.length === 0) {
          setNoStreamsYet(true)
        } else {
          setStreamPanels(result.streams)
          setNoStreamsYet(false)
        }

        lastTimestampRef.current = result.latestTimestamp
        totalMessagesRef.current = result.totalRetrieved
        setLimitReached(result.limitReached)
        setTotalMessages(result.totalRetrieved)
        setLoading(false)

        const finished = isJobFinished(job)
        if (!result.limitReached && !finished) {
          startAutoRefresh()
        }
      } catch (err) {
        if (!mountedRef.current) return
        const msg = err instanceof Error ? err.message : 'Unknown error'
        getLogger().error('Error loading cloud logs:', err)
        setError(msg)
        setLoading(false)
      }
    }
    load()

    return () => {
      mountedRef.current = false
      stopAutoRefresh()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId])

  // Stop auto-refresh if the job transitions to finished (parent re-renders with updated job)
  useEffect(() => {
    if (jobFinished) {
      stopAutoRefresh()
      setNoStreamsYet((prev) => (prev && streamPanels.length === 0 ? prev : false))
    }
  }, [jobFinished, stopAutoRefresh, streamPanels.length])

  const handleLoadMore = useCallback(async () => {
    setLoadingMore(true)
    setLimitReached(false)

    try {
      const result = await fetchLogs(sessionId, lastTimestampRef.current, GLOBAL_MAX_MESSAGES)
      if (!mountedRef.current) return

      if (result.streams.length > 0) {
        setStreamPanels((prev) => mergeStreamPanels(prev, result.streams))
        setNoStreamsYet(false)
      }
      lastTimestampRef.current = result.latestTimestamp
      setLimitReached(result.limitReached)
      addToTotal(result.totalRetrieved)

      if (!result.limitReached && !isJobFinished(job)) {
        startAutoRefresh()
      }
    } catch (err) {
      getLogger().error('Error loading more logs:', err)
      setLimitReached(true)
    } finally {
      setLoadingMore(false)
    }
  }, [sessionId, job, addToTotal, startAutoRefresh])

  const handleToggleStream = useCallback((streamName: string) => {
    setStreamPanels((prev) =>
      prev.map((s) => (s.streamName === streamName ? {...s, expanded: !s.expanded} : s))
    )
  }, [])

  // ---- Render ----

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
          minHeight: '200px',
        }}
      >
        <div className='spinner-border text-primary mb-3' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
        <div style={{fontSize: 'var(--ob-font-size-lg)', color: 'var(--ob-text-secondary)'}}>
          Retrieving logs...
        </div>
        <div
          style={{
            fontSize: 'var(--ob-font-size-base)',
            color: 'var(--ob-text-muted)',
            marginTop: '8px',
          }}
        >
          This may take a few moments
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{padding: 'var(--ob-spacing-4, 1rem)'}}>
        <div className='alert alert-danger' role='alert'>
          Error retrieving logs: {error}
        </div>
      </div>
    )
  }

  if (notFound) {
    return (
      <div style={{padding: 'var(--ob-spacing-4, 1rem)'}}>
        <div style={{fontSize: 'var(--ob-font-size-base)', color: 'var(--ob-text-secondary)'}}>
          No logs available for this session.
        </div>
      </div>
    )
  }

  if (noStreamsYet && !jobFinished) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
          minHeight: '200px',
        }}
      >
        <div className='spinner-border text-primary mb-3' role='status'>
          <span className='visually-hidden'>Waiting...</span>
        </div>
        <div style={{fontSize: 'var(--ob-font-size-lg)', color: 'var(--ob-text-secondary)'}}>
          Waiting for logs...
        </div>
        <div
          style={{
            fontSize: 'var(--ob-font-size-base)',
            color: 'var(--ob-text-muted)',
            marginTop: '8px',
          }}
        >
          The job is still running. Logs will appear as tasks produce output.
        </div>
      </div>
    )
  }

  if (streamPanels.length === 0) {
    return (
      <div style={{padding: 'var(--ob-spacing-4, 1rem)'}}>
        <div style={{fontSize: 'var(--ob-font-size-base)', color: 'var(--ob-text-secondary)'}}>
          No log streams found for this session.
        </div>
      </div>
    )
  }

  return (
    <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
      {/* Scrollable stream panels */}
      <div style={{flex: 1, overflow: 'auto', padding: 'var(--ob-spacing-4, 1rem)'}}>
        {streamPanels.map((stream) => (
          <StreamPanel key={stream.streamName} stream={stream} onToggle={handleToggleStream} />
        ))}
      </div>

      {/* Load more bar */}
      {limitReached && (
        <div
          style={{
            flexShrink: 0,
            borderTop: '1px solid #dee2e6',
            padding: '8px 0',
            textAlign: 'center',
          }}
        >
          {loadingMore ? (
            <div
              style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'}}
            >
              <div className='spinner-border spinner-border-sm text-primary' role='status' />
              <span
                style={{fontSize: 'var(--ob-font-size-base)', color: 'var(--ob-text-secondary)'}}
              >
                Loading...
              </span>
            </div>
          ) : (
            <button
              className='btn btn-link btn-sm'
              onClick={handleLoadMore}
              style={{fontSize: 'var(--ob-font-size-base)', textDecoration: 'none'}}
            >
              Load more logs... ({totalMessages} loaded)
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export {CloudLogsTabContent}
